import type { HappeningStoryPageData } from "./types";

// Lazy-load story modules so a single story page (or the archive card) does
// not bundle every other story's data + image imports into the same chunk.
const modules = import.meta.glob<{ default: HappeningStoryPageData }>("./*.ts");

const isStoryPath = (path: string) => {
  if (path.endsWith("/index.ts") || path.endsWith("/types.ts")) return false;
  const filename = path.split("/").pop() ?? "";
  return !filename.startsWith("_");
};

const slugFromPath = (path: string) =>
  (path.split("/").pop() ?? "").replace(/\.ts$/, "");

const loaderBySlug: Record<string, () => Promise<{ default: HappeningStoryPageData }>> = {};
for (const [path, loader] of Object.entries(modules)) {
  if (!isStoryPath(path)) continue;
  loaderBySlug[slugFromPath(path)] = loader;
}

const cache: Record<string, HappeningStoryPageData> = {};
const inflight: Record<string, Promise<HappeningStoryPageData | undefined>> = {};

export const hasStoryPageData = (slug: string): boolean =>
  Boolean(loaderBySlug[slug]);

export const allStoryPageSlugs = (): string[] =>
  Object.keys(loaderBySlug).sort();

/** Synchronous accessor — returns data only if already loaded (via loadStoryPageData). */
export const getStoryPageData = (slug: string): HappeningStoryPageData | undefined =>
  cache[slug];

export const loadStoryPageData = (
  slug: string,
): Promise<HappeningStoryPageData | undefined> => {
  if (cache[slug]) return Promise.resolve(cache[slug]);
  if (inflight[slug]) return inflight[slug];
  const loader = loaderBySlug[slug];
  if (!loader) return Promise.resolve(undefined);
  const p = loader().then((m) => {
    const data = m.default;
    if (data?.slug) cache[data.slug] = data;
    delete inflight[slug];
    return data;
  });
  inflight[slug] = p;
  return p;
};

export const loadAllStoryPageData = async (): Promise<
  Record<string, HappeningStoryPageData>
> => {
  await Promise.all(Object.keys(loaderBySlug).map((s) => loadStoryPageData(s)));
  return { ...cache };
};

export type { HappeningStoryPageData };
